import { GridApi } from "ag-grid-enterprise";
import { Modal } from "antd";
import ExportPdfPrintModal from "pages/print-view-layout/Export-Pdf-Print-Modal";

const DOM_CELL_LIMIT = 32000;
const MAX_NUMBER_OF_COLUMNS = 10;

export function PrintViewDialogContent({
  visibleColumnCount,
  exceedsRecommended = false
}: {
  visibleColumnCount?: number;
  exceedsRecommended?: boolean;
}) {
  return (
    <div>
      <p style={{ marginBottom: 12 }}>
        This layout is optimized for printing or saving as a PDF.
      </p>
      <p style={{ marginBottom: 12 }}>
        Use your browser&apos;s print settings to adjust scaling, paper size, or
        orientation. Reducing the number of visible columns can help content fit
        better on the page.
      </p>
      {exceedsRecommended && (
        <p style={{ marginBottom: 0 }}>
          This view exceeds the recommended print size for US Letter
          (Landscape). It has {visibleColumnCount} visible columns. For best
          results, limit to {MAX_NUMBER_OF_COLUMNS} or fewer columns when
          printing.
        </p>
      )}
      <p style={{ marginBottom: 8, fontWeight: 600 }}>
        Tips for better print results:
      </p>
      <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
        <li>
          Limit to {MAX_NUMBER_OF_COLUMNS} or fewer columns for best results
        </li>
        <li>
          In the print dialog, use <strong>Scale &gt; Fit to page</strong> to
          scale the content to fit on one page
        </li>
        <li>
          Change the <strong>Orientation</strong> (Portrait / Landscape) in the
          print dialog to better fit your table layout
        </li>
      </ul>
    </div>
  );
}

function PrintViewValidationModal({
  gridApi,
  onProceed,
  skipColumnIds = [],
  forceVisibleColumnIds = []
}: {
  gridApi: GridApi;
  onProceed: () => void;
  skipColumnIds?: string[];
  /** Columns hidden in the grid that the print view still renders. */
  forceVisibleColumnIds?: string[];
}) {
  const columns = gridApi
    .getAllDisplayedColumns()
    .filter((c) => !skipColumnIds.includes(c.getColId()));
  const displayedColIds = new Set(columns.map((c) => c.getColId()));
  const extraPrintedColumnCount = forceVisibleColumnIds.filter(
    (colId) => !displayedColIds.has(colId) && !!gridApi.getColumn(colId)
  ).length;
  const rowCount = gridApi.getDisplayedRowCount();
  const visibleColumnCount = columns.length + extraPrintedColumnCount;

  const effectiveRowCount = Math.ceil(rowCount);
  const totalCells = effectiveRowCount * visibleColumnCount;

  // Case 2: DOM limit - block, do not allow proceed
  if (totalCells > DOM_CELL_LIMIT) {
    Modal.warning({
      title: "Too much data for Export PDF/Print",
      content: (
        <div>
          <p>
            The grid has approximately{" "}
            <strong>
              {rowCount.toLocaleString()} rows × {visibleColumnCount} columns
            </strong>{" "}
            ({totalCells.toLocaleString()} cells).
          </p>
          <p>
            Browsers may crash when rendering this many DOM elements. Please:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>Uncheck some column visibility to reduce columns</li>
            <li>Apply filters to reduce the number of rows</li>
          </ul>
        </div>
      ),
      okText: "OK"
    });
    return;
  }

  ExportPdfPrintModal({
    content: (
      <PrintViewDialogContent
        visibleColumnCount={visibleColumnCount}
        exceedsRecommended={visibleColumnCount > MAX_NUMBER_OF_COLUMNS}
      />
    ),
    onProceed
  });
}

export default PrintViewValidationModal;
